import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';

import { OfferPage } from '~/components/offer/OfferPage';
import { useAnalytics } from '~/hooks/useAnalytics';
import { getApiBaseUrl } from '~/lib/api';
import { readFinalScore } from '~/lib/finalScore';
import type { PublicLandingPage } from '~/types';

export const Route = createFileRoute('/$slug/offer')({
  component: OfferRoute,
});

function OfferRoute() {
  const router = useRouter();
  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const { trackEvent } = useAnalytics({ slug });

  const handleForwardClick = useCallback(() => {
    trackEvent('offer_click');
  }, [trackEvent]);

  // Claim handoff: navigate the BROWSER (not the SPA router) to the public
  // claim endpoint. The server records the claim against the guest session
  // and 302-redirects to the advertiser's forward URL with `?yak_session=`.
  const handleClaimClick = useCallback(() => {
    const final = readFinalScore(slug);
    const sessionRef = final?.sessionRef;
    if (!sessionRef) {
      // No session ref available — fall through to direct forward URL so the
      // player still reaches the advertiser, just without claim attribution.
      if (landingPage.forwardUrl) {
        window.location.href = landingPage.forwardUrl;
      }
      return;
    }
    const base = getApiBaseUrl();
    const url = `${base}/v1/public/claim/${encodeURIComponent(sessionRef)}?slug=${encodeURIComponent(slug)}`;
    window.location.href = url;
  }, [slug, landingPage.forwardUrl]);

  const handlePlayAgain = useCallback(() => {
    if (landingPage.targetType === 'show') {
      router.navigate({ to: '/$slug/episodes', params: { slug } });
    } else {
      router.navigate({
        to: '/$slug/game/$episodeId',
        params: { slug, episodeId: landingPage.targetId },
      });
    }
  }, [router, slug, landingPage]);

  const handleSkip = useCallback(() => {
    router.navigate({ to: '/$slug/episodes', params: { slug } });
  }, [router, slug]);

  return (
    <OfferPage
      offerContent={landingPage.offerContent}
      branding={landingPage.branding}
      forwardUrl={landingPage.forwardUrl}
      title={landingPage.title}
      onClaimClick={handleClaimClick}
      onForwardClick={handleForwardClick}
      onOfferView={() => trackEvent('offer_view')}
      onSkip={handleSkip}
      targetType={landingPage.targetType}
      onPlayAgain={handlePlayAgain}
      showYakOffer={landingPage.showYakOffer}
    />
  );
}
